import classnames from 'classnames';
import type React from 'react';
import { memo, useEffect, useRef } from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import { type TrackType, useStore } from 'src/lib/player/store';
import { noop, onlyMouseEvents } from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import WebMercatorViewport from 'viewport-mercator-project';
import { POI_HEIGHT, POI_WIDTH } from '../Map/MarkerContext';

type Props = {
  track: TrackType;
  onClick: (track: TrackType, isSelectd: boolean) => void;
};

function drawTrack(
  track: TrackType,
  canvas: HTMLCanvasElement,
  scale: number = DEVICE_PIXEL_RATIO,
): void {
  const ctx = canvas.getContext('2d');
  if (ctx == null) return;

  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  const mercatorProjection = new WebMercatorViewport({ width: 16500, height: 10000 });
  const points = track.coordinates.map((lnglatZ) => mercatorProjection.project(lnglatZ));

  if (points.length > 0) {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const [x, y] of points) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    if (maxX > minX || maxY > minY) {
      const scale = maxX - minX > maxY - minY ? maxX - minX : maxY - minY;
      const scaleXY = (POI_WIDTH - 4) / scale;
      const offsetX = (POI_WIDTH - ((POI_WIDTH - 4) * (maxX - minX)) / scale) / 2;
      const offsetY = (POI_WIDTH - ((POI_WIDTH - 4) * (maxY - minY)) / scale) / 2;

      for (let i = 0; i < points.length; i++) {
        points[i][0] = (points[i][0] - minX) * scaleXY + offsetX;
        points[i][1] = (points[i][1] - minY) * scaleXY + offsetY;
      }

      ctx.globalAlpha = 1;
      ctx.strokeStyle = track.markerColor.hex;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';

      ctx.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
      }
      ctx.stroke();
    }
  }
}

const TrackIcon = memo(function TrackIcon({
  track,
  scale = 0.5,
}: {
  track: TrackType;
  scale?: number;
}): JSX.Element {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;

    drawTrack(track, canvas, DEVICE_PIXEL_RATIO);
  }, [track]);

  return (
    <canvas
      ref={canvasRef}
      width={POI_WIDTH * DEVICE_PIXEL_RATIO}
      height={POI_HEIGHT * DEVICE_PIXEL_RATIO}
      style={{ width: POI_WIDTH * scale, height: POI_HEIGHT * scale }}
    />
  );
});

export default function TrackListItem({ track, onClick = noop }: Props): JSX.Element {
  const label = track.name || t('track');

  const toggleHidden = useStore((state) => state.actions.toggleHidden);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);

  const isSelected = useStore((state) => state.timer.selected.includes(track.id));
  const isSelectedOrHighlighted = useStore(
    (state) => state.timer.highlighted === track.id || state.timer.selected.includes(track.id),
  );
  const isVisible = useStore((state) => !state.timer.hidden.has(track.id));
  const eventNames = useStore((state) =>
    state.currentEventIndices
      .filter((i) => state.events[i].tracks.some((f) => f.id === track.id))
      .map((i) => state.events[i].shortName)
      .join(', '),
  );
  const eventCount = useStore((state) => state.currentEventIndices.length);

  function handleClick() {
    if (isSelectedOrHighlighted) {
      sendAnalyticsEvent('track-list-item-click', 'track-unselect');
    } else {
      sendAnalyticsEvent('track-list-item-click', 'track-select');
    }
    toggleSelectedStarter(track.id);
    onClick(track, !isSelected);
  }

  function handleHideClick(ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
    ev.stopPropagation();
    toggleHidden(track.id);
  }

  return (
    <div
      className={classnames('starter-list-item', {
        selected: isSelectedOrHighlighted,
      })}
      {...onClickGenerator(handleClick)}
      onPointerEnter={onlyMouseEvents(() => {
        setHighlighted(track.id);
      })}
      onPointerLeave={onlyMouseEvents(() => {
        setHighlighted(null);
      })}
    >
      <div>
        <TrackIcon track={track} />
        <div className="starter-text" style={{ marginLeft: 5 }}>
          <div className="starter-name" title={label}>
            <div className="starter-name-content">{label}</div>
          </div>
          {eventCount > 1 && (
            <div className="starter-info" title={eventNames}>
              {eventNames}
            </div>
          )}
        </div>
        <div className="starter-controls">
          {isSelected ? <i className="fas fa-star" /> : <i className="far fa-star" />}
          {isVisible ? (
            <i className="far fa-eye" {...onClickGenerator(handleHideClick)} />
          ) : (
            <i className="far fa-eye-slash" {...onClickGenerator(handleHideClick)} />
          )}
        </div>
      </div>
    </div>
  );
}
